import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  DoCheck,
  effect,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";

import { AuthStore } from "../../core/auth";
import type { SessionActor } from "../../core/auth";
import { StorageService } from "../../core/storage";
import { ProductsApiService } from "../catalog/api/products-api.service";
import { ProductsStore } from "../catalog/store/products.store";
import { ProductUnitsStore } from "../catalog/store/product-units.store";
import { VariantsStore } from "../catalog/store/variants.store";
import { requiresStatusTransitionReason } from "../inventory/inventory-ui-helpers";
import { StockColumnPickerComponent } from "./stock-column-picker.component";
import { STOCK_OPT_IN_COLUMNS } from "./stock-columns";
import type { StockOptInColumnId } from "./stock-columns";
import { StockPriceRangeFilterComponent } from "./stock-price-range-filter.component";
import { StatusChangeModalComponent } from "./components/status-change-modal.component";
import {
  SaleChannel,
  SellUnitModalComponent,
} from "./components/sell-unit-modal.component";
import { StockFiltersComponent } from "./stock-filters.component";
import { StockStore } from "./stock.store";
import {
  isCatalogActivityVisible,
  StockTreeRowActionEvent,
  StockTreeTableComponent,
} from "./stock-tree-table.component";
import type {
  InventoryUnitStatus,
  StockTreeFilters,
  StockTreeNode,
} from "../inventory/models/inventory.model";
import { ToastService } from "../../shared/ui/toast/toast.service";

type PriceRange = { min: number; max: number };

const withOptionalFilter = <K extends keyof StockTreeFilters>(
  base: StockTreeFilters,
  key: K,
  value: StockTreeFilters[K] | undefined,
): StockTreeFilters => {
  if (value === undefined) {
    const next = { ...base } as Record<string, unknown>;
    delete next[key as string];
    return next as StockTreeFilters;
  }

  return { ...base, [key]: value } as StockTreeFilters;
};

@Component({
  selector: "app-stock-page",
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    StockColumnPickerComponent,
    StockPriceRangeFilterComponent,
    StockFiltersComponent,
    StockTreeTableComponent,
    StatusChangeModalComponent,
    SellUnitModalComponent,
  ],
  providers: [StockStore],
  template: `
    <div class="stock-page">
      <div class="stock-page__header">
        <h1>Productos</h1>

        <div class="stock-page__header-actions">
          @if (priceSliderVisible()) {
            @if (priceBounds(); as bounds) {
              <app-stock-price-range-filter
                [bounds]="bounds"
                [value]="priceRange()"
                (valueChange)="onPriceRangeChange($event)"
              />
            }
          }

          <app-stock-column-picker
            [selected]="visibleColumns()"
            [options]="STOCK_OPT_IN_COLUMNS"
            (visibilityChange)="onColumnToggle($event)"
          />

          <app-stock-filters
            (checkedStatusesChange)="onCheckedStatusesChange($event)"
            (includeInactiveChange)="onIncludeInactiveChange($event)"
            (brandChanged)="onBrandChanged($event)"
            (modelChanged)="onModelChanged($event)"
            (colorChanged)="onColorChanged($event)"
            (capacityChanged)="onCapacityChanged($event)"
            (supplierChanged)="onSupplierChanged($event)"
          />

          @if (canCreateProduct()) {
            <a
              class="stock-page__create-link app-action app-action--primary"
              routerLink="/catalog/new"
              >Nuevo producto</a
            >
          }
        </div>
      </div>

      @if (stockStore.error(); as errorMessage) {
        <p>{{ errorMessage }}</p>
      }

      @if (actionError(); as actionErrorMessage) {
        <p>{{ actionErrorMessage }}</p>
      }

      @if (stockStore.loading()) {
        <p>Cargando...</p>
      } @else {
        <app-stock-tree-table
          [nodes]="stockStore.tree()"
          [checkedStatuses]="checkedStatuses()"
          [includeInactive]="includeInactive()"
          [priceRange]="priceRange()"
          [visibleColumns]="visibleColumns()"
          [canManageCatalog]="canManageCatalog()"
          [canDeleteProduct]="canDeleteProduct()"
          (rowAction)="onRowAction($event)"
          (createVariantCta)="onCreateVariantCta($event)"
          (createUnitCta)="onCreateUnitCta($event)"
          (statusChangeRequest)="onUnitStatusChangeRequest($event)"
        />
      }

      <app-status-change-modal
        [visible]="!!pendingStatusChange() && pendingSaleChoice() === null"
        [currentStatus]="pendingStatusChange()?.currentStatus ?? 'AVAILABLE'"
        [nextStatus]="pendingStatusChange()?.nextStatus ?? 'AVAILABLE'"
        [unitLabel]="pendingStatusChange()?.unitLabel ?? ''"
        [requiresReason]="requiresReasonForPendingStatusChange()"
        [isSubmitting]="isSubmitting()"
        (confirm)="confirmQuickStatusChange($event)"
        (cancel)="cancelQuickStatusChange()"
      />

      <app-sell-unit-modal
        [visible]="pendingSaleChoice() !== null"
        [unitLabel]="pendingSaleChoice()?.unitLabel ?? ''"
        [isSubmitting]="isSubmitting()"
        (confirm)="confirmSaleChannel($event)"
        (cancel)="cancelSaleChannelChoice()"
      />

      @if (quickStatusError(); as errorMessage) {
        <p class="stock-page__error" role="alert">{{ errorMessage }}</p>
      }
    </div>
  `,
  styles: [
    `
      .stock-page {
        display: grid;
        gap: 16px;
        min-width: 0;
      }

      app-stock-tree-table {
        display: block;
        min-width: 0;
        max-width: 100%;
      }

      .stock-page__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
      }

      .stock-page__header h1 {
        margin: 0;
        color: #332b28;
        line-height: 1.1;
      }

      .stock-page__header-actions {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
      }

      .stock-page__create-link {
        flex-shrink: 0;
        white-space: nowrap;
      }

      @media (max-width: 720px) {
        .stock-page__create-link {
          width: 100%;
        }
      }

      .stock-page__error {
        color: #b91c1c;
        font-size: 0.875rem;
        margin: 0;
      }
    `,
  ],
})
export class StockPage implements OnInit, DoCheck {
  readonly stockStore = inject(StockStore);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly productsApi = inject(ProductsApiService);
  private readonly productsStore = inject(ProductsStore);
  private readonly variantsStore = inject(VariantsStore);
  private readonly storage = inject(StorageService);
  readonly actionError = signal<string | null>(null);
  /** Issue #63 — the page owns the visibility set; the table and the price bounds consume it. */
  readonly checkedStatuses = signal<Set<InventoryUnitStatus>>(
    new Set<InventoryUnitStatus>(["AVAILABLE"]),
  );
  readonly includeInactive = signal(false);
  readonly visibleColumns = signal<Set<StockOptInColumnId>>(
    new Set(STOCK_OPT_IN_COLUMNS.map((c) => c.id)),
  );
  readonly STOCK_OPT_IN_COLUMNS = STOCK_OPT_IN_COLUMNS;
  readonly productUnitsStore = inject(ProductUnitsStore);
  readonly toastService = inject(ToastService);
  readonly pendingStatusChange = signal<{
    unitId: string;
    currentStatus: InventoryUnitStatus;
    nextStatus: InventoryUnitStatus;
    unitLabel?: string;
  } | null>(null);
  readonly reasonDraft = signal("");
  readonly reasonError = signal<string | null>(null);
  readonly isSubmitting = signal(false);
  readonly quickStatusError = signal<string | null>(null);
  readonly lastFocusedTrigger = signal<HTMLElement | null>(null);
  /** Venta: elección de canal pendiente (STORE/WEB) antes de vender la unidad. */
  readonly pendingSaleChoice = signal<{
    unitId: string;
    unitLabel: string;
    reason: string;
  } | null>(null);
  readonly requiresReasonForPendingStatusChange = computed(() => {
    const pending = this.pendingStatusChange();
    return pending
      ? requiresStatusTransitionReason(
          pending.currentStatus,
          pending.nextStatus,
        )
      : false;
  });
  readonly canCreateProduct = computed(() => {
    const actor = this.authStore.actor();
    return actor?.role === "SUPER_ADMIN" || actor?.role === "STORE_ADMIN";
  });
  readonly canManageCatalog = computed(() => this.canCreateProduct());
  readonly canDeleteProduct = computed(
    () => this.authStore.actor()?.role === "SUPER_ADMIN",
  );
  readonly priceSliderVisible = computed(() =>
    this.visibleColumns().has("precio"),
  );
  private readonly priceSelection = signal<{
    tree: StockTreeNode[];
    range: PriceRange;
  } | null>(null);
  readonly priceBounds = computed<PriceRange | null>(() => {
    const checked = this.checkedStatuses();
    const includeInactive = this.includeInactive();
    const prices: number[] = [];

    for (const product of this.stockStore.tree()) {
      // Issue #63 — the bounds follow the table's visible set: the Inactivos
      // gate hides inactive products/variants and keeps their prices out.
      if (!isCatalogActivityVisible(product.isActive, includeInactive)) {
        continue;
      }

      for (const variant of product.children) {
        if (!isCatalogActivityVisible(variant.isActive, includeInactive)) {
          continue;
        }

        for (const unit of variant.children) {
          // Issue #63 — unchecked statuses (including SOLD) never shape the bounds.
          if (checked.has(unit.status) && typeof unit.price === "number") {
            prices.push(unit.price);
          }
        }
      }
    }

    if (prices.length === 0) {
      return null;
    }

    return { min: Math.min(...prices), max: Math.max(...prices) };
  });
  readonly priceRange = computed<PriceRange | null>(() => {
    const selection = this.priceSelection();

    // A tree reload replaces the array identity → selection is stale → inactive.
    if (selection === null || selection.tree !== this.stockStore.tree()) {
      return null;
    }

    if (!this.priceSliderVisible()) {
      return null;
    }

    const bounds = this.priceBounds();
    if (bounds === null) {
      return null;
    }

    // Selection spanning the full bounds counts as inactive.
    if (
      selection.range.min === bounds.min &&
      selection.range.max === bounds.max
    ) {
      return null;
    }

    return selection.range;
  });
  private filters: StockTreeFilters = {};
  private lastScopedStoreId: string | null = null;

  constructor() {
    // Read: on actor change, load stored column preference
    effect(() => {
      const actor = this.authStore.actor();
      if (!actor) return;
      const stored = this.storage.get<StockOptInColumnId[]>(
        this.storageKey(actor),
      );
      if (stored) {
        const validIds = stored.filter((id): id is StockOptInColumnId =>
          STOCK_OPT_IN_COLUMNS.some((c) => c.id === id),
        );
        this.visibleColumns.set(new Set(validIds));
      }
    });

    // Write: on column change, persist to storage
    effect(() => {
      const actor = this.authStore.actor();
      if (!actor) return;
      this.storage.set(
        this.storageKey(actor),
        Array.from(this.visibleColumns()),
      );
    });
  }

  onColumnToggle(cols: Set<StockOptInColumnId>): void {
    this.visibleColumns.set(cols);

    if (!cols.has("precio")) {
      this.priceSelection.set(null);
    }
  }

  onPriceRangeChange(range: PriceRange | null): void {
    if (range === null) {
      this.priceSelection.set(null);
      return;
    }

    const bounds = this.priceBounds();
    if (bounds === null) {
      return;
    }

    if (range.min === bounds.min && range.max === bounds.max) {
      this.priceSelection.set(null);
      return;
    }

    this.priceSelection.set({ tree: this.stockStore.tree(), range });
  }

  private storageKey(actor: SessionActor): string {
    const storeSegment = actor.assignedStoreId ?? "__none__";
    return `ultrastock.stock-columns.${actor.userId}.${storeSegment}`;
  }

  ngOnInit(): void {
    if (this.authStore.actor()?.role === "SUPER_ADMIN") {
      this.stockStore.loadTree();
      return;
    }

    this.syncScopedStore();
  }

  ngDoCheck(): void {
    this.syncScopedStore();
  }

  /** Issue #63 — status checkboxes are client-side; only Inactivos reloads the tree. */
  onCheckedStatusesChange(statuses: Set<InventoryUnitStatus>): void {
    this.checkedStatuses.set(statuses);
  }

  onIncludeInactiveChange(checked: boolean): void {
    this.includeInactive.set(checked);

    if (checked) {
      this.filters = { ...this.filters, includeInactive: true };
    } else {
      this.filters = withOptionalFilter(
        this.filters,
        "includeInactive",
        undefined,
      );
    }

    this.stockStore.loadTree(this.filters);
  }

  onBrandChanged(brand: string): void {
    this.filters = withOptionalFilter(
      this.filters,
      "brand",
      brand || undefined,
    );
    this.stockStore.loadTree(this.filters);
  }

  onModelChanged(model: string): void {
    this.filters = withOptionalFilter(
      this.filters,
      "model",
      model || undefined,
    );
    this.stockStore.loadTree(this.filters);
  }

  onColorChanged(color: string): void {
    this.filters = withOptionalFilter(
      this.filters,
      "color",
      color || undefined,
    );
    this.stockStore.loadTree(this.filters);
  }

  onCapacityChanged(capacity: string): void {
    this.filters = withOptionalFilter(
      this.filters,
      "capacity",
      capacity || undefined,
    );
    this.stockStore.loadTree(this.filters);
  }

  onSupplierChanged(supplierId: string): void {
    this.filters = withOptionalFilter(
      this.filters,
      "supplierId",
      supplierId || undefined,
    );
    this.stockStore.loadTree(this.filters);
  }

  onRowAction(event: StockTreeRowActionEvent): void {
    if (event.action === "edit") {
      this.handleEditAction(event);
      return;
    }

    this.handleDeleteAction(event);
  }

  onCreateVariantCta(productId: string): void {
    void this.router.navigate(["/catalog", productId, "edit"], {
      state: { activeTab: "VARIANTS", createVariant: true },
    });
  }

  onCreateUnitCta(event: { productId: string; variantId: string }): void {
    void this.router.navigate(["/catalog", event.productId, "edit"], {
      state: { activeTab: "UNITS", createUnit: true },
    });
  }

  private syncScopedStore(): void {
    const actor = this.authStore.actor();

    if (actor?.role === "SUPER_ADMIN") {
      return;
    }

    const activeStoreId = this.authStore.activeStoreId();

    if (activeStoreId === this.lastScopedStoreId) {
      return;
    }

    this.lastScopedStoreId = activeStoreId;
    this.filters = withOptionalFilter(
      this.filters,
      "storeId",
      activeStoreId ?? undefined,
    );
    this.stockStore.loadTree(this.filters);
  }

  onUnitStatusChangeRequest(event: {
    unitId: string;
    currentStatus: InventoryUnitStatus;
    nextStatus: InventoryUnitStatus;
    unitLabel?: string;
  }): void {
    const pending = this.pendingStatusChange();
    const unitLabel = event.unitLabel ?? pending?.unitLabel ?? "";
    this.pendingStatusChange.set({ ...event, unitLabel });
    this.reasonDraft.set("");
    this.reasonError.set(null);
  }

  cancelQuickStatusChange(): void {
    const pending = this.pendingStatusChange();
    const trigger = this.lastFocusedTrigger();
    this.pendingStatusChange.set(null);
    this.reasonDraft.set("");
    this.reasonError.set(null);
    this.quickStatusError.set(null);
    if (pending) {
      this.resetInlineStatusSelect(pending.unitId, pending.currentStatus);
    }
    if (trigger) {
      trigger.focus();
    }
  }

  private resetInlineStatusSelect(
    unitId: string,
    originalStatus: InventoryUnitStatus,
  ): void {
    const select = document.querySelector<HTMLSelectElement>(
      `.stock-tree-table__status-select[data-unit-id="${unitId}"]`,
    );
    if (select && select.value !== originalStatus) {
      select.value = originalStatus;
    }
  }

  confirmQuickStatusChange(reason: string): void {
    const pending = this.pendingStatusChange();
    if (!pending) return;

    const reasonToUse = reason ?? this.reasonDraft().trim();
    const needsReason = requiresStatusTransitionReason(
      pending.currentStatus,
      pending.nextStatus,
    );
    if (needsReason && !reasonToUse.trim()) {
      this.reasonError.set(
        "Necesitás un motivo para transiciones hacia o desde MISSING.",
      );
      return;
    }

    // Venta: el canal (STORE/WEB) es obligatorio antes de vender.
    if (pending.nextStatus === "SOLD") {
      this.pendingSaleChoice.set({
        unitId: pending.unitId,
        unitLabel: pending.unitLabel ?? pending.unitId,
        reason: reasonToUse.trim(),
      });
      return;
    }

    this.isSubmitting.set(true);
    this.quickStatusError.set(null);

    const unitLabel = pending.unitLabel ?? pending.unitId;
    this.productUnitsStore
      .changeStatus(pending.unitId, {
        status: pending.nextStatus,
        reason: reasonToUse.trim(),
      })
      .subscribe({
        next: () => {
          this.pendingStatusChange.set(null);
          this.reasonDraft.set("");
          this.reasonError.set(null);
          this.isSubmitting.set(false);
          this.stockStore.loadTree(this.filters);
          this.toastService.success("Estado actualizado", unitLabel);
        },
        error: (err: unknown) => {
          this.isSubmitting.set(false);
          this.quickStatusError.set(
            resolveActionError(
              err,
              "No pudimos actualizar el estado de la unidad.",
            ),
          );
          this.toastService.error(
            "Error al actualizar",
            "No pudimos cambiar el estado de la unidad.",
          );
        },
      });
  }

  confirmSaleChannel(channel: SaleChannel): void {
    const pendingSale = this.pendingSaleChoice();
    if (!pendingSale) return;

    this.isSubmitting.set(true);
    this.quickStatusError.set(null);

    this.productUnitsStore
      .sellUnit(pendingSale.unitId, {
        saleChannel: channel,
        ...(pendingSale.reason ? { reason: pendingSale.reason } : {}),
      })
      .subscribe({
        next: () => {
          this.pendingSaleChoice.set(null);
          this.pendingStatusChange.set(null);
          this.reasonDraft.set("");
          this.reasonError.set(null);
          this.isSubmitting.set(false);
          this.stockStore.loadTree(this.filters);
          this.toastService.success("Venta registrada", pendingSale.unitLabel);
        },
        error: (err: unknown) => {
          this.isSubmitting.set(false);
          this.quickStatusError.set(
            resolveActionError(err, "No pudimos registrar la venta."),
          );
          this.toastService.error(
            "Error al registrar la venta",
            "No pudimos registrar la venta.",
          );
        },
      });
  }

  cancelSaleChannelChoice(): void {
    this.pendingSaleChoice.set(null);
  }

  private handleEditAction(event: StockTreeRowActionEvent): void {
    this.actionError.set(null);

    if (event.level === "product" && event.productId) {
      void this.router.navigate(["/catalog", event.productId, "edit"]);
      return;
    }

    if (event.level === "variant" && event.productId && event.variantId) {
      void this.router.navigate([
        "/catalog",
        event.productId,
        "variant",
        event.variantId,
        "edit",
      ]);
      return;
    }

    if (
      event.level === "unit" &&
      event.productId &&
      event.variantId &&
      event.unitId
    ) {
      void this.router.navigate([
        "/catalog",
        event.productId,
        "variant",
        event.variantId,
        "unit",
        event.unitId,
        "edit",
      ]);
    }
  }

  private handleDeleteAction(event: StockTreeRowActionEvent): void {
    if (event.level === "unit") {
      if (!event.unitId) {
        return;
      }

      const unitLabel = event.unitLabel ?? event.unitId;
      if (
        !globalThis.confirm(
          `¿Estás seguro de que querés eliminar la unidad "${unitLabel}"?`,
        )
      ) {
        return;
      }

      this.actionError.set(null);
      this.productUnitsStore.delete(event.unitId).subscribe({
        complete: () => this.reloadTree(),
        error: (error: unknown) =>
          this.actionError.set(
            resolveActionError(error, "No pudimos eliminar la unidad."),
          ),
      });
      return;
    }

    if (event.level === "product" && event.productId) {
      if (this.canDeleteProduct()) {
        if (
          !globalThis.confirm(
            "Esta accion eliminara todas sus variantes y unidades. Continuar?",
          )
        ) {
          return;
        }

        this.actionError.set(null);
        this.productsStore.delete(event.productId).subscribe({
          complete: () => this.reloadTree(),
          error: (error: unknown) =>
            this.actionError.set(
              resolveActionError(error, "No pudimos eliminar el producto."),
            ),
        });
        return;
      }

      if (
        !globalThis.confirm(
          "Se va a archivar o restaurar el producto. Continuar?",
        )
      ) {
        return;
      }

      this.actionError.set(null);
      this.productsApi.toggleStatus(event.productId).subscribe({
        next: () => this.reloadTree(),
        error: (error: unknown) =>
          this.actionError.set(
            resolveActionError(error, "No pudimos actualizar el producto."),
          ),
      });
      return;
    }

    if (event.productId && event.variantId) {
      if (
        !globalThis.confirm("Se va a eliminar la variante. ¿Querés continuar?")
      ) {
        return;
      }

      this.actionError.set(null);
      this.variantsStore.delete(event.productId, event.variantId).subscribe({
        next: () => this.reloadTree(),
        error: (error: unknown) =>
          this.actionError.set(
            resolveActionError(error, "No pudimos eliminar la variante."),
          ),
      });
    }
  }

  private reloadTree(): void {
    this.stockStore.loadTree(this.filters);
  }
}

const resolveActionError = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};
